import { useForm, FormProvider } from 'react-hook-form'
import { useEffect, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import styles from './styles.module.scss'
import { SubmitButton } from '@components/ui/Button'
import ButtonMinimal from '@components/ui/Button/ButtonMinimal'
import { AddressFormWrapper } from './AddressFormWrapper'
import { useAuth } from '@api/accounts/auth/hooks/useAuth'
import { ErrorField } from './shared/InputField/InputErrorMessage'
import { addressAdd } from '../api/addressAdd'
import { SubmitButtonState } from '@components/ui/Button/SubmitButton'
import { getErrorMessage } from '@api/graphqlShopify'
import {
  AddAddressData,
  AddAddressSchema,
  AddressFormFields
} from './shared/AddressFormFields'

export function AddAddress({
  handleCloseAccountDetailsDrawer
}: {
  handleCloseAccountDetailsDrawer: () => void
}) {
  const [isProvincesLoading, setIsProvincesLoading] = useState(false)
  const [buttonState, setButtonState] = useState<SubmitButtonState>('disabled')
  const { refreshUserProfile, makeRequestWithRefreshCheck } = useAuth()
  const methods = useForm<AddAddressData & { customError?: string }>({
    mode: 'onChange',
    shouldFocusError: true,
    resolver: zodResolver(AddAddressSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      address1: '',
      address2: '',
      city: '',
      zip: '',
      zoneCode: '',
      territoryCode: 'GB',
      defaultAddress: false
    }
  })
  const formValues = methods.getValues()
  const formErrors = methods.formState.errors

  const handleAddAddress = async (data: AddAddressData) => {
    setButtonState('loading')
    try {
      await makeRequestWithRefreshCheck(addressAdd, data)
      await refreshUserProfile()

      setTimeout(() => {
        setButtonState('ready')
        handleCloseAccountDetailsDrawer()
      }, 2000)
    } catch (e) {
      const error = e as unknown as Error
      const errorMessage = getErrorMessage(error) || 'Something went wrong'

      methods.setError(
        'customError',
        {
          type: 'focus',
          message: errorMessage
        },
        {
          shouldFocus: true
        }
      )
      setButtonState('ready')
    }
  }

  useEffect(() => {
    methods.register('territoryCode', { required: true })
  }, [methods])

  useEffect(() => {
    const isDisabled =
      isProvincesLoading ||
      (!methods.formState.isDirty && methods.formState.isValid)
    setButtonState(isDisabled ? 'disabled' : 'ready')
  }, [isProvincesLoading, methods.formState.isValid, methods.formState.isDirty])

  return (
    <FormProvider {...methods}>
      <AddressFormWrapper title="Add Address">
        <form onSubmit={methods.handleSubmit(handleAddAddress)}>
          <AddressFormFields
            setIsProvincesLoading={setIsProvincesLoading}
            formValues={formValues}
            formSetFieldValue={methods.setValue}
          />
          <div className={styles.AccountForm__Buttons}>
            {formErrors.customError?.message && (
              <ErrorField message={formErrors.customError.message} />
            )}
            <ButtonMinimal
              underlined={true}
              onClick={() => handleCloseAccountDetailsDrawer()}
            >
              Cancel
            </ButtonMinimal>
            <SubmitButton
              state={buttonState}
              disabled={buttonState === 'disabled'}
              type="submit"
              fullWidth="never"
            >
              Save
            </SubmitButton>
          </div>
        </form>
      </AddressFormWrapper>
    </FormProvider>
  )
}
